import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, friendlyError, setOnSessionExpired, tokenStorage, unwrap } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      const data = unwrap(res);
      if (mounted.current) setUser(data);
      return data;
    } catch (err) {
      if (mounted.current) setUser(null);
      tokenStorage.clear();
      throw err;
    }
  }, []);

  // Bootstrap: si hay token guardado, intenta refrescar
  useEffect(() => {
    (async () => {
      const token = tokenStorage.get();
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await fetchMe();
      } catch {
        /* token invalido / expirado */
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
  }, [fetchMe]);

  // 401 handler global
  useEffect(() => {
    setOnSessionExpired(() => {
      if (!mounted.current) return;
      setUser(null);
      setExpired(true);
    });
    return () => setOnSessionExpired(null);
  }, []);

  const login = useCallback(async ({ email, password, rememberMe = true }) => {
    try {
      const res = await api.post('/auth/login', {
        email,
        password,
        remember_me: rememberMe,
      });
      const data = unwrap(res);
      tokenStorage.set(data.token.access_token, rememberMe);
      setUser(data.user);
      setExpired(false);
      return data.user;
    } catch (err) {
      const message = friendlyError(err);
      const e = new Error(message);
      e.code = err?.payload?.error || err?.response?.data?.error;
      throw e;
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const res = await api.post('/auth/register', payload);
      return unwrap(res);
    } catch (err) {
      const message = friendlyError(err);
      const e = new Error(message);
      e.code = err?.payload?.error || err?.response?.data?.error;
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* ignore */
    }
    tokenStorage.clear();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      expired,
      isAuthenticated: !!user,
      isAdmin: !!user?.is_admin,
      isSuperAdmin: !!user?.is_super_admin,
      login,
      register,
      logout,
      fetchMe,
      clearExpired: () => setExpired(false),
    }),
    [user, loading, expired, login, register, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
